import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Wifi, Filter, Sliders, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { selectionModalVariants, contentVariants, buttonHoverVariants } from './utils/animations';
import OperatorSelector from './components/OperatorSelector';
import PriceSelector from './components/PriceSelector';
import SpeedSelector from './components/SpeedSelector';
import useComparisonStore from './store/comparisonStore';
import { useClarityModalTracking } from '../../hooks/useClarityModalTracking';
import { trackEvent, trackJourneyStep } from '../../config/clarity';

/**
 * SelectionModal component
 * 1/4 width modal that slides in from the right
 * Allows users to select criteria for package comparison
 *
 * Now using Zustand for state management
 */
const SelectionModal = ({
  isOpen,
  onClose
}) => {
  // Get state and actions from the Zustand store
  const {
    selectedOperators,
    setSelectedOperators,
    resetFilters,
    recommendedSpeed,
    priceRange,
    speedRange,
    setPriceRange,
    setSpeedRange,
    openRecommendationModal,
    openComparisonModal,
    availableOperators
  } = useComparisonStore();
  
  // Local UI state
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  // Track modal open/close
  const { trackAction } = useClarityModalTracking('selection_modal', isOpen, {
    selectedOperatorsCount: selectedOperators?.length || 0,
    priceRange: priceRange ? `${priceRange[0]}-${priceRange[1]}` : 'default',
    speedRange: speedRange ? `${speedRange[0]}-${speedRange[1]}` : 'default'
  });
  
  // Check if we're on mobile
  const isMobile = windowWidth < 768;
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save the current scroll position
      const scrollY = window.scrollY;
      
      // Prevent scrolling
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore scrolling when component unmounts or modal closes
        document.body.style.overflow = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);
  
  // Track when modal opens and ensure operators are selected
  useEffect(() => {
    if (isOpen) {
      console.log('SelectionModal opened with operators:', selectedOperators);
      
      // Track modal open with journey step
      trackJourneyStep('package_selection', 'selecting_criteria', {
        selectedOperatorsCount: selectedOperators?.length || 0
      });
      
      // Check if we're coming from recommendation
      const isComingFromRecommendation = localStorage.getItem('comingFromRecommendation') === 'true';
      
      // If we're coming from recommendation and no operators are selected, select all operators
      // Use a ref to track if we've already tried to select all operators to prevent infinite loops
      if (isComingFromRecommendation && selectedOperators.length === 0) {
        console.log('Coming from recommendation but no operators selected, selecting all operators');
        
        // Get the current state of the store
        const { selectAllOperators, selectedOperators: currentOperators } = useComparisonStore.getState();
        
        // Only call selectAllOperators if we actually need to (double-check)
        if (currentOperators.length === 0) {
          // Set a flag in localStorage to prevent multiple calls
          if (localStorage.getItem('operatorsSelectionInProgress') !== 'true') {
            localStorage.setItem('operatorsSelectionInProgress', 'true');
            
            // Call selectAllOperators and then clear the flag
            selectAllOperators();
            
            // Clear the flag after a short delay
            setTimeout(() => {
              localStorage.removeItem('operatorsSelectionInProgress');
            }, 500);
          }
        }
      }
    }
  }, [isOpen]); // Remove selectedOperators from dependencies to prevent infinite loop
  
  // Watch for operator selection changes and open comparison modal
  useEffect(() => {
    // Check if operators selection is in progress to prevent interference with the first useEffect
    if (localStorage.getItem('operatorsSelectionInProgress') === 'true') {
      console.log("Skipping comparison modal effect because operators selection is in progress");
      return;
    }
    
    // IMPORTANT: We need to get the CURRENT state each time this effect runs
    // to ensure we have the latest value of isRecommendationModalOpen
    const { isRecommendationModalOpen } = useComparisonStore.getState();
    
    console.log("SelectionModal effect triggered:", {
      isOpen,
      selectedOperatorsCount: selectedOperators.length,
      isRecommendationModalOpen,
      selectedOperators
    });
    
    // IMPORTANT: Only open comparison modal if:
    // 1. Selection modal is open
    // 2. Operators are selected
    // 3. Recommendation modal is NOT open
    // 4. We're not in the middle of a transition from recommendation to selection
    // 5. We're not in the middle of selecting operators
    
    // Check if we're in a transition from recommendation to selection
    const isComingFromRecommendation = localStorage.getItem('comingFromRecommendation') === 'true';
    
    // Add an additional check to prevent opening the comparison modal if the recommendation modal was just opened
    const wasRecommendationJustOpened = localStorage.getItem('recommendationJustOpened') === 'true';
    
    // Use a debounce mechanism to prevent rapid firing of this effect
    // Only proceed if we haven't recently tried to open the comparison modal
    const lastComparisonAttempt = localStorage.getItem('lastComparisonAttempt');
    const now = Date.now();
    const comparisonDebounceTime = 1000; // 1 second debounce
    
    if (lastComparisonAttempt && now - parseInt(lastComparisonAttempt) < comparisonDebounceTime) {
      console.log("Debouncing comparison modal opening");
      return;
    }
    
    if (isOpen &&
        selectedOperators.length > 0 &&
        !isRecommendationModalOpen &&
        !isComingFromRecommendation &&
        !wasRecommendationJustOpened) {
      
      // Set the last attempt timestamp
      localStorage.setItem('lastComparisonAttempt', now.toString());
      
      console.log("Auto-opening comparison modal with operators:", selectedOperators);
      
      // Open the comparison modal when operators are selected
      // Use a longer delay to ensure the UI updates first
      const timer = setTimeout(() => {
        // Double-check that recommendation modal is still not open right before opening comparison
        const { isRecommendationModalOpen: currentRecommendationState } = useComparisonStore.getState();
        if (!currentRecommendationState) {
          openComparisonModal();
        } else {
          console.log("Prevented comparison modal from opening because recommendation modal is now open");
        }
        
        // Clear the timestamp after the attempt
        localStorage.removeItem('lastComparisonAttempt');
      }, 300);
      
      return () => {
        clearTimeout(timer);
        localStorage.removeItem('lastComparisonAttempt');
      };
    }
  }, [isOpen, selectedOperators, openComparisonModal]);
  
  // Toggle filters expanded state
  const toggleFilters = () => {
    // Track filter toggle
    trackEvent('filters_toggle', {
      newState: !filtersExpanded ? 'expanded' : 'collapsed'
    });
    
    setFiltersExpanded(!filtersExpanded);
  };
  
  // Handle recommendation button click
  const handleRecommendClick = () => {
    // Track recommendation request
    trackEvent('recommendation_requested', {
      source: 'selection_modal',
      selectedOperatorsCount: selectedOperators?.length || 0
    });
    
    trackJourneyStep('package_selection', 'requesting_recommendation', {
      fromSelectionModal: true
    });
    
    openRecommendationModal();
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - only on mobile or when not side-by-side with comparison modal */}
          {isMobile && (
            <motion.div
              className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm"
              style={{ zIndex: 54 }} // High z-index for backdrop, but below the modal
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                trackAction('backdrop_close');
                onClose();
              }}
            />
          )}
          
          {/* Modal */}
          <motion.div
            className={`fixed top-0 bottom-0 right-0 bg-gradient-to-b from-gray-900 to-gray-800 shadow-2xl border-l border-gray-800 flex flex-col z-50 ${
              isMobile ? 'w-full' : 'w-1/4'
            }`}
            style={{
              zIndex: 55, // Much higher z-index to ensure it's above ComparisonModal
              width: isMobile ? '100%' : 'calc(25% - 1px)',
              boxShadow: '0 0 15px rgba(0, 0, 0, 0.3)' /* Add stronger shadow for better visibility */
            }}
            variants={selectionModalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Header */}
            <motion.div 
              className="p-4 border-b border-gray-800 flex justify-between items-center"
              variants={contentVariants}
            >
              <div className="flex items-center">
                <Wifi className="h-5 w-5 text-purple-400 mr-2" />
                <h2 className="text-xl font-bold text-white">Compare Packages</h2>
              </div>
              <button 
                className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-800/50 transition-colors"
                onClick={() => {
                  trackAction('x_button_close');
                  onClose();
                }}
              >
                <X className="h-5 w-5" />
              </button>
            </motion.div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Operator Selection */}
              <motion.div
                className="mb-6"
                variants={contentVariants}
              >
                <OperatorSelector operators={availableOperators} />
              </motion.div>
              
              {/* Filters Toggle (on mobile) */}
              {isMobile && (
                <motion.button
                  className="w-full py-2.5 px-4 bg-gray-800 text-white rounded-lg font-medium flex items-center justify-center mb-6"
                  onClick={toggleFilters}
                  variants={buttonHoverVariants}
                  initial="initial"
                  whileHover="hover"
                  whileTap="tap"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  <span>{filtersExpanded ? "Hide Filters" : "Show Filters"}</span>
                  {filtersExpanded ? 
                    <ChevronUp className="h-4 w-4 ml-2" /> : 
                    <ChevronDown className="h-4 w-4 ml-2" />
                  }
                </motion.button>
              )}
              
              {/* Filters - always visible on desktop, toggleable on mobile */}
              {(!isMobile || filtersExpanded) && (
                <motion.div 
                  className="space-y-6"
                  variants={contentVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {/* Filter Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Sliders className="h-4 w-4 text-purple-400 mr-2" />
                      <h3 className="text-sm font-medium text-white">Filters</h3>
                    </div>
                    <button
                      className="text-xs text-purple-400 hover:text-purple-300"
                      onClick={() => {
                        // Track filter reset
                        trackEvent('filters_reset', {
                          previousPriceRange: priceRange ? `${priceRange[0]}-${priceRange[1]}` : 'default',
                          previousSpeedRange: speedRange ? `${speedRange[0]}-${speedRange[1]}` : 'default'
                        });
                        
                        resetFilters();
                      }}
                    >
                      Reset Filters
                    </button>
                  </div>
                  
                  {/* Price Range Filter */}
                  <div className="mb-6">
                    <PriceSelector />
                  </div>
                  
                  {/* Speed Range Filter */}
                  <div className="mb-6">
                    <SpeedSelector />
                  </div>
                </motion.div>
              )}
              
              {/* Recommendation Option */}
              <motion.div
                className="mt-6 bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-xl p-4 border border-purple-800/30"
                variants={contentVariants}
              >
                <div className="flex items-center mb-2">
                  <Zap className="h-5 w-5 text-purple-400 mr-2" />
                  <h3 className="text-lg font-medium text-white">Not sure what speed you need?</h3>
                </div>
                <p className="text-sm text-gray-300 mb-4">
                  Answer a few questions about your internet usage and we'll recommend the perfect speed for you.
                </p>
                <motion.button
                  className="w-full py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium"
                  onClick={handleRecommendClick}
                  variants={buttonHoverVariants}
                  initial="initial"
                  whileHover="hover"
                  whileTap="tap"
                >
                  Get a Recommendation
                </motion.button>
              </motion.div>
            </div>
            
            {/* Footer with info text */}
            <motion.div
              className="p-4 border-t border-gray-800"
              variants={contentVariants}
            >
              {selectedOperators.length === 0 ? (
                <p className="text-xs text-gray-500 text-center">
                  Please select at least one network operator to see packages
                </p>
              ) : (
                <p className="text-xs text-gray-500 text-center">
                  {selectedOperators.length} operator{selectedOperators.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SelectionModal;